'use client'

import { useQuery } from '@tanstack/react-query'
import { useParams } from 'next/navigation'
import { createContext, useContext, useMemo } from 'react'
import { useTRPC } from '@/app/_trpc/config'
import type { ARTICLE_STATE } from './types'

type Ctx = {
  states: Record<string, ARTICLE_STATE>
  collectionSlug: string | null
}

const ArticleStateContext = createContext<Ctx | null>(null)

export function ArticleStateProvider({ children }: { children: React.ReactNode }) {
  const params = useParams<{ collectionSlug?: string }>()
  const collectionSlug = params.collectionSlug ?? null

  const trpc = useTRPC()
  const { data } = useQuery(
    trpc.docs.getCollectionStructure.queryOptions(
      {
        collectionSlug: collectionSlug ?? ''
      },
      {
        enabled: !!collectionSlug,
        staleTime: 10 * 60 * 1000
      }
    )
  )

  const states = useMemo(() => {
    if (!data?.modules) return {}

    const statesMap: Record<string, ARTICLE_STATE> = {}

    for (const mod of data.modules) {
      for (const article of mod.articles) {
        if (article.readingStatus) {
          statesMap[article.id] = article.readingStatus
        }
      }
    }

    return statesMap
  }, [data?.modules])

  return (
    <ArticleStateContext.Provider value={{ states, collectionSlug }}>
      {children}
    </ArticleStateContext.Provider>
  )
}

export function useArticleStatesContext() {
  const ctx = useContext(ArticleStateContext)
  if (!ctx) throw new Error('useArticleStatesContext must be inside ArticleStateProvider')
  return ctx
}
